import React, { useEffect, useState, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, ZoomControl, useMap } from 'react-leaflet';
import { useSearchParams } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import * as Leaflet from 'leaflet';
import api from '../../api/axios';
import { MagnifyingGlassIcon, XMarkIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid';
import { HeartIcon as HeartOutline,
        Squares2X2Icon,
        UserGroupIcon,
        CakeIcon,
        ShoppingBagIcon,
        WrenchScrewdriverIcon,
        HeartIcon,
        SparklesIcon,
        AcademicCapIcon,
        CpuChipIcon,
        PaintBrushIcon,
        ShareIcon
       } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { MapPinIcon, ClockIcon, GlobeAltIcon, PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/solid';
import HoursInput from '../../utils/HoursInput';
import { summarizeBusinessHours, perDayBusinessHours } from '../../utils/HoursFormat';

// --- Icons Setup ---
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const PLACEHOLDER_IMAGE = "https://external-content.duckduckgo.com/iu/?u=https%3A%2F%2Fc1.staticflickr.com%2F9%2F8568%2F30436737336_4bc59f3a5c_b.jpg&f=1&nofb=1&ipt=01553680de38ac50186df9c76516d05329fadba76025609f0ade7763180cc1ee?auto=format&fit=crop&q=80&w=400";

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const ZURICH = { lat: 47.3717597, lng: 8.5426102 };

const TIER_COLORS = {
  'General':    '#64748b',
  'Gastronomy': '#ef4444',
  'Shops':      '#3b82f6',
  'Services':   '#0f172a',
  'Health':     '#10b981',
  'Welness':    '#ec4899',
  'Education':  '#f59e0b',
  'Technical':  '#6366f1',
  'Culture':    '#8b5cf6',
};
const DEFAULT_COLOR = '#64748b';

const categoryTypes = [
{ key: 'Gastronomy', labelKey: 'map.gastronomy' },
{ key: 'Shops', labelKey: 'map.shops' },
{ key: 'Health', labelKey: 'map.health' },
{ key: 'Education', labelKey: 'map.education' },
{ key: 'Services', labelKey: 'map.services' },
{ key: 'Wellness', labelKey: 'map.wellness' },
{ key: 'Technical', labelKey: 'map.technical' },
{ key: 'Culture', labelKey: 'map.culture' },
];

const TIER_INITIALS = {
  'General':    '',
  'Gastronomy': '',
  'Shops':      '',
  'Services':   '',
  'Health':     '',
  'Welness':    '',
  'Education':  '',
  'Technical':  '',
  'Culture':    '',
};

const TIER_META = {
  'All':        { icon: Squares2X2Icon,         color: '#1d5cab' },
  'General':    { icon: UserGroupIcon,           color: '#64748b' },
  'Gastronomy': { icon: CakeIcon,                color: '#ef4444' },
  'Shops':      { icon: ShoppingBagIcon,         color: '#3b82f6' },
  'Services':   { icon: WrenchScrewdriverIcon,   color: '#0f172a' },
  'Health':     { icon: HeartIcon,               color: '#10b981' },
  'Wellness':   { icon: SparklesIcon,            color: '#ec4899' },
  'Education':  { icon: AcademicCapIcon,         color: '#f59e0b' },
  'Technical':  { icon: CpuChipIcon,             color: '#6366f1' },
  'Culture':    { icon: PaintBrushIcon,          color: '#8b5cf6' },
};

const DEFAULT_BUSINESS_HOURS = () => {
  const days = {};
  DAYS.forEach(d => { days[d] = { opening: '', closing: '', isOpen: false }; });
  return { isOpen247: false, variableHours: false, ...days };
};

let DefaultIcon = Leaflet.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});
Leaflet.Marker.prototype.options.icon = DefaultIcon;


// ─── Main Component ───────────────────────────────────────────────────────────
const MapView = () => {
  const { t: translate } = useTranslation();
  const [searchParams] = useSearchParams();
  const [providers, setProviders] = useState([]);
  const [userPos, setUserPos] = useState([ZURICH.lat, ZURICH.lng]);
  const [highlightProvider, setHighlightProvider] = useState(null);
  const hasDeepLink = useRef(!!searchParams.get('provider'));
  const [isAddingMode, setIsAddingMode] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');

  const [addressQuery, setAddressQuery] = useState('');
  const [isSearchingAddr, setIsSearchingAddr] = useState(false);

  const [selectedTiers, setSelectedTiers] = useState([]);

  const handleMapClick = (lat, lng) => {
    setNewLocation([lat, lng]);
    fetchAddressFromCoords(lat, lng);
  };

  const [newLocation, setNewLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '', desc: '', tier: 'General',
    phone: '', email: '', preference: 'email',
    website: '', address: '',
    businessHours: DEFAULT_BUSINESS_HOURS()
  });

  const { user } = useAuth();
  const [favorites, setFavorites] = useState([]);

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!hasDeepLink.current) {
      if (user) {
        if (user.defaultLocation?.lat && user.defaultLocation?.lng) {
          setUserPos([user.defaultLocation.lat, user.defaultLocation.lng]);
        }
      } else if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((pos) => {
          if (!hasDeepLink.current) {
            setUserPos([pos.coords.latitude, pos.coords.longitude]);
          }
        });
      } else {
        setUserPos([ZURICH.lat, ZURICH.lng]);
      }
    }
    fetchProviders();
  }, [user]);

  const fetchProviders = async () => {
    try {
      const res = await api.get('/map/providers');
      setProviders(res.data);
    } catch (err) { console.error(err); }
  };

  // Deep-link: auto-open provider popup when ?provider=<id> is in the URL
  useEffect(() => {
    if (providers.length === 0 || highlightProvider) return;
    const pid = searchParams.get('provider');
    if (!pid) return;
    const match = providers.find(p => p._id === pid);
    if (match && match.location?.coordinates) {
      setHighlightProvider(match);
    }
  }, [providers, searchParams, highlightProvider]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open suggest form when ?suggest=true is in the URL (e.g. from landing page)
  useEffect(() => {
    if (searchParams.get('suggest') === 'true' && !isAddingMode) {
      startAddingProcess();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAddressFromCoords = async (lat, lng) => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
      const data = await res.json();
      if (data?.display_name) {
        setFormData(prev => ({ ...prev, address: data.display_name }));
        setAddressQuery(data.display_name);
      }
    } catch (err) { console.error('Reverse geocode failed', err); }
  };

  const handleAddressSearch = async () => {
    if (!addressQuery) return;
    setIsSearchingAddr(true);
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressQuery)}`);
      const data = await response.json();
      if (data?.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lng = parseFloat(data[0].lon);
        setNewLocation([lat, lng]);
        setFormData(prev => ({ ...prev, address: data[0].display_name }));
        setAddressQuery(data[0].display_name);
      } else {
        alert(translate('general.alerts.address-not-found'));
      }
    } catch (err) {
      console.error(err);
      alert(translate('general.alerts.error-geocoding'));
    } finally {
      setIsSearchingAddr(false);
    }
  };

const mapDataproviderData = useMemo(() => {
  // If no search query and no specific filters are selected, return all
  if (!searchQuery && selectedTiers.length === 0) return providers;

  return providers.filter(p => {
    // 1. Tier Filter (Multi-select logic)
    // If selectedTiers is empty, it passes. 
    // If not empty, the provider's tier must be inside the selectedTiers array.
    const matchesTier = selectedTiers.length === 0 || selectedTiers.includes(p.tier);
    if (!matchesTier) return false;

    // 2. Search Query Filter
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    const nameMatch = p.businessName?.toLowerCase().includes(query);
    const descMatch = p.details?.description?.toLowerCase().includes(query);
    const tagsMatch = p.details?.tags?.some(tag => tag.toLowerCase().includes(query));

    return nameMatch || descMatch || tagsMatch;
  });

}, [providers, searchQuery, selectedTiers]);

  // Group providers that share the same coordinate (rounded to 4 dp ≈ 11 m).
  // Single-item groups render as normal pins; multi-item groups render as cluster badges.
  const groupedProviders = useMemo(() => {
    const buckets = new Map();
    mapDataproviderData.forEach(p => {
      if (!p.location?.coordinates) return;
      const [lng, lat] = p.location.coordinates;
      const key = `${Math.round(lat * 1e4) / 1e4},${Math.round(lng * 1e4) / 1e4}`;
      const arr = buckets.get(key) || [];
      arr.push(p);
      buckets.set(key, arr);
    });
    return Array.from(buckets.values());
  }, [mapDataproviderData]);

  const startAddingProcess = () => {
    setIsAddingMode(true);
    setIsDrawerOpen(true);
    setNewLocation(null);
    setAddressQuery('');
  };

  const cancelAdding = () => {
    setIsAddingMode(false);
    setIsDrawerOpen(false);
    setNewLocation(null);
    setFormData({ name: '', desc: '', tier: 'General', phone: '', email: '', preference: 'email', website: '', address: '', businessHours: DEFAULT_BUSINESS_HOURS() });
  };

  function normalizeBusinessHours(bh) {
    const norm = { isOpen247: !!bh.isOpen247, variableHours: !!bh.variableHours };
    DAYS.forEach(d => {
      const day = bh[d] || { opening: '', closing: '', isOpen: false };
      norm[d] = { opening: day.isOpen && day.opening ? day.opening : '', closing: day.isOpen && day.closing ? day.closing : '', isOpen: !!day.isOpen };
    });
    if (norm.isOpen247) {
      DAYS.forEach(d => { norm[d] = { opening: '', closing: '', isOpen: true }; });
    }
    return norm;
  }

  function validateBusinessHours(bh) {
    if (bh.isOpen247) return null;
    for (const d of DAYS) {
      if (bh[d].isOpen) {
        if (!bh[d].opening) return `${d}: opening time required`;
        if (!bh[d].closing) return `${d}: closing time required`;
      }
    }
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newLocation) return alert(translate('alerts.alert-submit-location'));
    if (user?.role !== 'provider' && !formData.email.trim() && !formData.phone.trim()) {
      return alert(translate('map.contact-required'));
    }
    setIsLoading(true);
    try {
      const normalizedHours = normalizeBusinessHours(formData.businessHours || {});
      const hoursError = validateBusinessHours(normalizedHours);
      if (hoursError) { alert(hoursError); setIsLoading(false); return; }

      const payload = {
        businessName: formData.name, description: formData.desc,
        lat: newLocation[0], lng: newLocation[1], address: formData.address,
        tier: formData.tier, phone: formData.phone, email: formData.email,
        preference: formData.preference, website: formData.website,
        businessHours: normalizeBusinessHours(formData.businessHours)
      };

      if (user?.role !== 'provider') {
        await api.post('/map/suggest', payload);
      } else {
        await api.post('/map/save-listing', payload);
      }

      setTimeout(() => {
        alert(translate('alerts.alert-submit-success'));
        setIsLoading(false);
        cancelAdding();
      }, 800);
    } catch (err) {
      console.error(err);
      alert(translate('alerts.alert-submit-location'));
      setIsLoading(false);
    }
  };

  const handleToggleFav = async (providerId) => {
    if (!user) return alert(translate('general.alerts.save-favourites'));
    try {
      if (favorites.includes(providerId)) {
        setFavorites(prev => prev.filter(id => id !== providerId));
      } else {
        setFavorites(prev => [...prev, providerId]);
      }
      await api.put(`/user/favorite/${providerId}`);
    } catch (err) {
      console.error('Error toggling favorite:', err);
      if (favorites.includes(providerId)) {
        setFavorites(prev => [...prev, providerId]);
      } else {
        setFavorites(prev => prev.filter(id => id !== providerId));
      }
    }
  };


        // ─── Enhanced Custom Map Pin ──────────────────────────────────────────────────
        const createCustomIcon = (color, tier) => {
          const translatedTier = translate(`map.${tier}`, tier);
          const initial = TIER_INITIALS[tier] || (translatedTier ? translatedTier[0] : '•');
          const isEmoji = initial.length > 1 || /\p{Emoji}/u.test(initial);
          return Leaflet.divIcon({
            className: '',
            html: `
              <div style="position:relative; width:40px; height:48px; filter: drop-shadow(0 4px 8px ${color}55);">
                <div style="
                  width: 40px; height: 40px;
                  background: linear-gradient(135deg, ${color}, ${color}cc);
                  border-radius: 50% 50% 50% 0;
                  transform: rotate(-45deg);
                  border: 3px solid white;
                  box-shadow: 0 2px 0 ${color}40;
                "></div>
                <span style="
                  position: absolute;
                  top: 7px; left: 0; width: 40px;
                  text-align: center;
                  font-size: ${isEmoji ? '14px' : '13px'};
                  font-weight: 800;
                  color: white;
                  line-height: 1;
                  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
                  pointer-events: none;
                ">${initial}</span>
              </div>
            `,
            iconSize: [40, 48],
            iconAnchor: [20, 48],
            popupAnchor: [0, -52],
          });
        };
        
        const pendingIcon = Leaflet.divIcon({
          className: '',
          html: `
            <div style="
              width: 44px; height: 44px;
              background: linear-gradient(135deg, #7c3aed, #a78bfa);
              border-radius: 50%;
              border: 3px solid white;
              box-shadow: 0 0 0 4px #7c3aed44, 0 4px 12px #7c3aed66;
              display: flex; align-items: center; justify-content: center;
              animation: pulse-ring 1.5s ease-in-out infinite;
            ">
              <span style="color:white; font-size:20px;">📍</span>
            </div>
          `,
          iconSize: [44, 44],
          iconAnchor: [22, 44],
          popupAnchor: [0, -48],
        });
        
        // ─── Cluster icon (multi-provider at same coordinate) ─────────────────────────
        const createClusterIcon = (count, color) => Leaflet.divIcon({
          className: '',
          html: `
            <div style="
              width: 44px; height: 44px;
              background: ${color};
              border-radius: 50%;
              border: 3px solid white;
              box-shadow: 0 2px 0 ${color}40, 0 4px 14px ${color}88;
              display: flex; align-items: center; justify-content: center;
            ">
              <span style="color:white;font-size:16px;font-weight:800;line-height:1;letter-spacing:-0.5px">${count}</span>
            </div>
          `,
          iconSize: [44, 44],
          iconAnchor: [22, 44],
          popupAnchor: [0, -48],
        });

        const clusterColor = (group) => {
          const tier = group[0].tier;
          return group.every(p => p.tier === tier)
            ? (TIER_COLORS[tier] || DEFAULT_COLOR)
            : '#1d5cab';
        };

        // ─── Helpers ──────────────────────────────────────────────────────────────────
        const LocationSelector = ({ isAdding, onLocationFound }) => {
          useMapEvents({
            click(e) {
              if (isAdding) onLocationFound(e.latlng.lat, e.latlng.lng);
            },
          });
          return null;
        };
        
        const MapRecenter = ({ location }) => {
          const map = useMap();
          useEffect(() => {
            if (location) map.flyTo(location, 13, { duration: 1.5 });
          }, [location, map]);
          return null;
        };

        const FlyToProvider = ({ provider, onDone }) => {
          const map = useMap();
          useEffect(() => {
            if (!provider || !provider.location?.coordinates) return;
            const [lng, lat] = provider.location.coordinates;
            const target = [lat, lng];
            map.flyTo(target, 16, { duration: 1.2 });
            const openPopup = () => {
              map.eachLayer((layer) => {
                if (layer.getPopup && layer.getLatLng) {
                  const pos = layer.getLatLng();
                  if (Math.abs(pos.lat - target[0]) < 0.001 && Math.abs(pos.lng - target[1]) < 0.001) {
                    layer.openPopup();
                  }
                }
              });
              if (onDone) onDone();
            };
            map.once('moveend', openPopup);
            return () => map.off('moveend', openPopup);
          }, [provider, map, onDone]);
          return null;
        };
        
        function HoursPreview({ summary, perDay }) {
          const [open, setOpen] = React.useState(false);
          return (
            <div className="text-sm">
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(s => !s); }}
                className="text-sm text-left font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                aria-expanded={open}
              >
                {summary}
                <span style={{ fontSize: '10px', opacity: 0.7 }}>{open ? '▲' : '▼'}</span>
              </button>
              {open && perDay && (
                <div className="mt-2 rounded-lg overflow-hidden border border-gray-100" style={{ background: '#f8fafc' }}>
                  {perDay.map(({ day, label }) => (
                    <div key={day} className="flex gap-3 px-3 py-1.5 border-b border-gray-100 last:border-0">
                      <span className="w-10 text-xs font-bold text-gray-400 uppercase">{translate(`map.days.${day.toLowerCase()}`)}</span>
                      <span className="text-xs text-gray-700">{label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        }
        
        // ─── Enhanced Popup Card ──────────────────────────────────────────────────────
        const ProviderCard = ({ p, favoriteIds, handleToggle, translate }) => {
          const providerColor = TIER_COLORS[p.tier] || DEFAULT_COLOR;
          const bh = p.businessHours || p.details?.businessHours || null;
          const summary = bh ? summarizeBusinessHours(bh, translate) : null;
          const perDay = bh ? perDayBusinessHours(bh, translate) : null;
          const isFav = favoriteIds.includes(p._id);
          const [isDescriptionExpanded, setIsDescriptionExpanded] = React.useState(false);
          const descRef = React.useRef(null);
          const [isClamped, setIsClamped] = React.useState(false);

          React.useEffect(() => {
            const el = descRef.current;
            if (el && !isDescriptionExpanded) setIsClamped(el.scrollHeight > el.clientHeight + 1);
          }, [p.details?.description, isDescriptionExpanded]);

          return (
            <div style={{ fontFamily: "'DM Sans', sans-serif", minWidth: '280px' }}>
              {/* — Image with gradient overlay — */}
              <div style={{ position: 'relative', margin: '-1px -1px 0', overflow: 'hidden', borderRadius: '12px 12px 0 0', width: '100%', aspectRatio: '16 / 9' }}>
                <img
                  src={p.photoUrl || PLACEHOLDER_IMAGE}
                  alt={p.businessName}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.5s ease' }}
                  onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
                  onMouseEnter={e => e.target.style.transform = 'scale(1.05)'}
                  onMouseLeave={e => e.target.style.transform = 'scale(1)'}
                />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 55%)' }} />
                <span style={{
                  position: 'absolute', bottom: '10px', left: '10px',
                  background: providerColor, color: 'white',
                  fontSize: '10px', fontWeight: '800', letterSpacing: '0.06em',
                  padding: '3px 10px', borderRadius: '99px', textTransform: 'uppercase',
                }}>
                  {translate(p.tier ? `map.${p.tier.toLowerCase()}` : 'map.general')}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); handleToggle(p._id); }}
                  style={{
                    position: 'absolute', top: '10px', right: '10px',
                    background: isFav ? '#ef4444' : 'rgba(255,255,255,0.85)',
                    border: 'none', borderRadius: '50%',
                    width: '32px', height: '32px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', backdropFilter: 'blur(4px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    transition: 'transform 0.15s ease, background 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.15)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                >
                  {isFav
                    ? <HeartSolid style={{ width: '16px', height: '16px', color: 'white' }} />
                    : <HeartOutline style={{ width: '16px', height: '16px', color: '#ef4444' }} />
                  }
                </button>
              </div>

              {/* — Card Body — */}
              <div style={{ padding: '14px 16px 16px' }}>
                <h3 style={{ margin: '0 0 6px', fontSize: '17px', fontWeight: '700', color: '#0f172a', lineHeight: 1.3 }}>
                  {p.businessName}
                </h3>

                {p.details?.description && (
                  <>
                    <p ref={descRef} style={{
                      margin: '0 0 12px', fontSize: '13px', color: '#64748b', lineHeight: 1.6,
                      display: '-webkit-box', WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: isDescriptionExpanded ? 'none' : 4,
                      overflow: 'hidden',
                    }}>
                      {p.details.description}
                    </p>
                    {(isClamped || isDescriptionExpanded) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setIsDescriptionExpanded(prev => !prev); }}
                        style={{
                          background: 'none', border: 'none', fontSize: '12px', fontWeight: '600',
                          cursor: 'pointer', padding: '0', textDecoration: 'underline', transition: '0.2s',
                          color: isDescriptionExpanded ? '#ef4444' : '#3b82f6',
                        }}
                      >
                        {translate(isDescriptionExpanded ? 'hero.read-less' : 'hero.read')}
                      </button>
                    )}
                  </>
                )}

                <div style={{ height: '1px', background: '#f1f5f9', margin: '0 0 10px' }} />

                <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                  {p.location?.address && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <div style={{ width: '18px', height: '18px', borderRadius: '5px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>
                        <MapPinIcon style={{ width: '11px', height: '11px', color: '#94a3b8' }} />
                      </div>
                      <span style={{ fontSize: '12px', color: '#475569', lineHeight: 1.45 }}>{p.location.address}</span>
                    </div>
                  )}

                  {summary && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <div style={{ width: '18px', height: '18px', borderRadius: '5px', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                        <ClockIcon style={{ width: '11px', height: '11px', color: '#10b981' }} />
                      </div>
                      <HoursPreview summary={summary} perDay={perDay} />
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    {p.contact?.phone && (
                      <a href={`tel:${p.contact.phone}`} title={p.contact.phone} style={{ textDecoration: 'none' }}>
                        <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <PhoneIcon style={{ width: '14px', height: '14px', color: '#22c55e' }} />
                        </div>
                      </a>
                    )}
                    {p.contact?.email && (
                      <a href={`mailto:${p.contact.email}`} title={p.contact.email} style={{ textDecoration: 'none' }}>
                        <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <EnvelopeIcon style={{ width: '14px', height: '14px', color: '#3b82f6' }} />
                        </div>
                      </a>
                    )}
                    {p.details?.website && (
                      <a
                        href={/^https?:\/\//i.test(p.details.website) ? p.details.website : `https://${p.details.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}
                        onMouseEnter={e => (e.currentTarget.querySelector('.website-text').style.textDecoration = 'underline')}
                        onMouseLeave={e => (e.currentTarget.querySelector('.website-text').style.textDecoration = 'none')}
                      >
                        <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <GlobeAltIcon style={{ width: '14px', height: '14px', color: '#3b82f6' }} />
                        </div>
                        <span className="website-text" style={{ fontSize: '12px', color: '#3b82f6', fontWeight: '600' }}>
                          {translate('map.visit-website')}
                        </span>
                      </a>
                    )}
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        const url = `${window.location.origin}/map?provider=${p._id}`;
                        if (navigator.share) {
                          try {
                            await navigator.share({ title: p.businessName, text: p.businessName, url });
                          } catch (err) {
                            if (err.name !== 'AbortError') {
                              await navigator.clipboard.writeText(url);
                              toast.success(translate('map.link-copied'));
                            }
                          }
                        } else {
                          await navigator.clipboard.writeText(url);
                          toast.success(translate('map.link-copied'));
                        }
                      }}
                      title={translate('map.share')}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                    >
                      <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ShareIcon style={{ width: '14px', height: '14px', color: '#8b5cf6' }} />
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        };

        const ShieldedMarkers = ({ groupedProviders = [], favorites = [], onToggleFav, translateFn }) => {
          const translate = translateFn || ((k) => k);
          const [selectedProvider, setSelectedProvider] = React.useState(null);

          if (!groupedProviders.length) return null;

          return groupedProviders.map((group) => {
            if (!group[0]?.location?.coordinates) return null;
            const [lng, lat] = group[0].location.coordinates;
            const position = [lat, lng];

            if (group.length === 1) {
              const p = group[0];
              const customIcon = createCustomIcon(TIER_COLORS[p.tier] || DEFAULT_COLOR, p.tier);
              return (
                <Marker key={p._id} position={position} icon={customIcon}>
                  <Popup className="map-popup-custom" minWidth={280} maxWidth={320}>
                    <ProviderCard
                      p={p}
                      favoriteIds={favorites}
                      handleToggle={onToggleFav}
                      translate={translate}
                    />
                  </Popup>
                </Marker>
              );
            }

            const groupKey = `${lat},${lng}`;
            const icon = createClusterIcon(group.length, clusterColor(group));
            const active = selectedProvider?.key === groupKey ? selectedProvider.p : null;

            return (
              <Marker
                key={groupKey}
                position={position}
                icon={icon}
                eventHandlers={{ popupclose: () => setSelectedProvider(null) }}
              >
                <Popup className="map-popup-custom" minWidth={280} maxWidth={320}>
                  {active ? (
                    <div style={{ fontFamily: "'DM Sans', sans-serif", minWidth: '280px' }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedProvider(null); }}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', padding: '10px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#3b82f6', width: '100%' }}
                      >
                        <ChevronRightIcon style={{ width: '14px', height: '14px', transform: 'rotate(180deg)' }} />
                        {translate('map.group-back')}
                      </button>
                      <ProviderCard
                        p={active}
                        favoriteIds={favorites}
                        handleToggle={onToggleFav}
                        translate={translate}
                      />
                    </div>
                  ) : (
                    <div style={{ fontFamily: "'DM Sans', sans-serif", minWidth: '280px', padding: '12px 0 4px' }}>
                      <p style={{ margin: '0 16px 10px', fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>
                        {translate('map.group-title').replace('{n}', group.length)}
                      </p>
                      {group.map((p) => {
                        const color = TIER_COLORS[p.tier] || DEFAULT_COLOR;
                        return (
                          <button
                            key={p._id}
                            onClick={(e) => { e.stopPropagation(); setSelectedProvider({ key: groupKey, p }); }}
                            style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '9px 16px', background: 'none', border: 'none', borderTop: '1px solid #f1f5f9', cursor: 'pointer', textAlign: 'left' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}
                          >
                            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: color, flexShrink: 0 }} />
                            <span style={{ flex: 1, fontSize: '13px', fontWeight: '600', color: '#0f172a' }}>{p.businessName}</span>
                            <span style={{ fontSize: '10px', fontWeight: '700', color, textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>
                              {translate(p.tier ? `map.${p.tier.toLowerCase()}` : 'map.general')}
                            </span>
                            <ChevronRightIcon style={{ width: '14px', height: '14px', color: '#94a3b8', flexShrink: 0 }} />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </Popup>
              </Marker>
            );
          });
        };

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="relative h-full w-full overflow-hidden flex flex-col">

      {/* ── Global Style Injections ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');

        .map-popup-custom .leaflet-popup-content-wrapper {
          padding: 0 !important;
          border-radius: 14px !important;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.1) !important;
          border: 1px solid rgba(255,255,255,0.6);
        }
        .map-popup-custom .leaflet-popup-content {
          margin: 0 !important;
          width: auto !important;
        }
        .map-popup-custom .leaflet-popup-tip-container {
          margin-top: -1px;
        }
        .leaflet-popup-close-button {
          display: none !important;
        }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }

        /* Desktop: top category row + result badge visible; mobile strip + FAB hidden */
        .mobile-category-strip { display: none; }
        .mobile-fab { display: none; }

        @media (max-width: 767px) {
          /* Mobile: bottom strip + FAB visible; top category row + result badge hidden */
          .mobile-category-strip { display: block; }
          .mobile-fab { display: block; }
          .desktop-category-row { display: none !important; }
          /* Prevent iOS Safari from zooming in on focused inputs */
          input, textarea, select { font-size: 16px !important; }
        }

        @keyframes pulse-ring {
          0%   { box-shadow: 0 0 0 0 rgba(124,58,237,0.4), 0 4px 12px rgba(124,58,237,0.4); }
          70%  { box-shadow: 0 0 0 10px rgba(124,58,237,0), 0 4px 12px rgba(124,58,237,0.4); }
          100% { box-shadow: 0 0 0 0 rgba(124,58,237,0), 0 4px 12px rgba(124,58,237,0.4); }
        }

        @keyframes slideInLeft {
          from { transform: translateX(-100%); opacity: 0; }
          to   { transform: translateX(0);     opacity: 1; }
        }

        .map-input:focus {
          outline: none;
          border-color: #1d5cab !important;
          box-shadow: 0 0 0 3px rgba(29,92,171,0.12) !important;
        }

        .tier-pill {
          transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .tier-pill:hover {
          transform: translateY(-1px);
        }
        .tier-pill.active {
          transform: translateY(-1px) scale(1.04);
        }

      `}</style>
                
      {/* ─── SEARCH BAR + RESULT COUNT ─── */}
        <div style={{
          position: 'absolute',
          top: isMobile ? '14px' : '24px',
          left: '50%', transform: 'translateX(-50%)',
          zIndex: 1100,
          width: isMobile ? 'calc(100% - 24px)' : 'calc(100% - 40px)',
          maxWidth: '500px', pointerEvents: 'none',
        }}>
          <div style={{
            pointerEvents: 'auto', display: 'flex', alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)',
            height: isMobile ? '48px' : '52px', padding: '0 16px', borderRadius: '16px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.12)', border: '1px solid rgba(255,255,255,0.7)',
          }}>
            <MagnifyingGlassIcon style={{ width: '20px', color: '#94a3b8' }} />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={translate('search.placeholder')}
              style={{
                flex: 1, border: 'none', outline: 'none', background: 'transparent',
                padding: '0 12px', fontSize: '16px', fontWeight: '500', color: '#1e293b'
              }}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                <XMarkIcon style={{ width: '18px', color: '#64748b' }} />
              </button>
            )}
          </div>
        </div>

        {/* RESULT COUNT BADGE — below category row on desktop, below search bar on mobile */}
        {(searchQuery || selectedTiers.length > 0) && (
          <div style={{
            position: 'absolute',
            top: isMobile ? '68px' : '130px',
            left: '50%', transform: 'translateX(-50%)',
            zIndex: 1100, pointerEvents: 'none', textAlign: 'center',
          }}>
            <span style={{
              background: 'rgba(30, 41, 59, 0.8)', color: 'white',
              padding: '4px 12px', borderRadius: '20px', fontSize: '11px',
              fontWeight: '700', backdropFilter: 'blur(4px)',
            }}>
              {mapDataproviderData.length} {translate(mapDataproviderData.length === 1 ? 'map.results-singular' : 'map.results-plural')}
            </span>
          </div>
        )}
        
        {/* ─── CATEGORY FILTER ROW (below search bar, desktop only) ─── */}
        <div className="desktop-category-row" style={{
          position: 'absolute',
          top: '84px',
          left: '50%', transform: 'translateX(-50%)',
          zIndex: 1100,
          pointerEvents: 'none',
        }}>
          <div className="no-scrollbar" style={{
            pointerEvents: 'auto', display: 'flex', alignItems: 'center',
            gap: '6px', overflowX: 'auto', padding: '4px',
          }}>
            {/* All / clear button */}
            <button
              onClick={() => setSelectedTiers([])}
              aria-label={translate('map.all')}
              title={translate('map.all')}
              className="tier-pill"
              style={{
                flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '38px', height: '38px', borderRadius: '10px', cursor: 'pointer',
                background: selectedTiers.length === 0 ? '#1d5cab' : 'rgba(255,255,255,0.92)',
                color: selectedTiers.length === 0 ? 'white' : '#64748b',
                border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <Squares2X2Icon style={{ width: '18px', height: '18px' }} />
            </button>

            {Object.entries(TIER_META).map(([tier, meta]) => {
              if (tier === 'All') return null;
              const isActive = selectedTiers.includes(tier);
              const Icon = meta.icon;
              return (
                <button
                  key={tier}
                  onClick={() => setSelectedTiers(prev =>
                    prev.includes(tier) ? prev.filter(t => t !== tier) : [...prev, tier]
                  )}
                  aria-label={translate(`map.${tier.toLowerCase()}`)}
                  title={translate(`map.${tier.toLowerCase()}`)}
                  className="tier-pill"
                  style={{
                    flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: '38px', height: '38px', borderRadius: '10px', cursor: 'pointer',
                    background: isActive ? meta.color : 'rgba(255,255,255,0.92)',
                    color: isActive ? 'white' : '#64748b',
                    border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <Icon style={{ width: '18px', height: '18px' }} />
                </button>
              );
            })}

            {/* + Suggest service button */}
            <button
              onClick={startAddingProcess}
              aria-label={translate('map.add-service-hint')}
              title={translate('map.add-service-hint')}
              className="tier-pill"
              style={{
                flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '38px', height: '38px', borderRadius: '10px', cursor: 'pointer',
                background: 'rgba(255,255,255,0.92)', color: '#64748b',
                border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '18px', height: '18px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        </div>

      {/* ══ SIDE DRAWER — bottom sheet on mobile, left panel on desktop ══════════ */}
      <div style={{
        position: 'absolute',
        zIndex: 2000,
        background: 'white',
        fontFamily: "'DM Sans', sans-serif",
        display: 'flex', flexDirection: 'column',
        transition: 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)',
        // Mobile: slides up from the bottom, 85 % of viewport height, top 15 % stays as map
        // Desktop: slides in from the left, full height
        ...(isMobile ? {
          bottom: 0, left: 0, right: 0, top: 'auto',
          width: '100%', maxWidth: 'none',
          height: '85%',
          borderRadius: '20px 20px 0 0',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
          transform: isDrawerOpen ? 'translateY(0)' : 'translateY(100%)',
        } : {
          top: 0, left: 0, height: '100%',
          width: '100%', maxWidth: '400px',
          borderRadius: 0,
          boxShadow: '8px 0 40px rgba(0,0,0,0.14)',
          transform: isDrawerOpen ? 'translateX(0)' : 'translateX(-100%)',
        }),
      }}>

        {/* Mobile drag handle */}
        {isMobile && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px', flexShrink: 0 }}>
            <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: '#cbd5e1' }} />
          </div>
        )}

        {/* Drawer Header */}
        <div style={{
          padding: isMobile ? '14px 20px 12px' : '24px 24px 20px',
          background: 'linear-gradient(135deg, #1d5cab 0%, #2372bd 100%)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          flexShrink: 0,
        }}>
          <div>
            <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: '700', color: 'rgba(255,255,255,0.65)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              {translate('map.business-directory')}
            </p>
            <h3 style={{ margin: 0, fontSize: isMobile ? '18px' : '22px', fontWeight: '800', color: 'white' }}>
              {translate('map.add-service')}
            </h3>
            <p style={{ margin: '6px 0 0', fontSize: '13px', fontWeight: '400', color: 'rgba(255,255,255,0.75)' }}>
               {translate('map.required-fields-notice')}
            </p>
          </div>
          <button
            onClick={cancelAdding}
            style={{
              width: '36px', height: '36px', borderRadius: '50%',
              background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.25)',
              color: 'white', cursor: 'pointer', fontSize: '20px', fontWeight: '300',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backdropFilter: 'blur(4px)', transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          >×</button>
        </div>

        {/* Form wraps the scrollable body + sticky footer so <button type="submit"> works */}
        <form onSubmit={handleSubmit} style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>

          {/* Scrollable fields */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>

            {/* Step 1: Location */}
            <div style={{
              borderRadius: '12px', marginBottom: '20px', overflow: 'hidden',
              border: `2px solid ${newLocation ? '#10b981' : '#1d5cab'}`,
              transition: 'border-color 0.3s ease',
            }}>
              <div style={{
                padding: '12px 16px', background: newLocation ? '#f0fdf4' : '#eff6ff',
                display: 'flex', alignItems: 'center', gap: '10px',
              }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                  background: newLocation ? '#10b981' : '#1d5cab',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontSize: '13px', fontWeight: '800',
                }}>
                  {newLocation ? '✓' : '1'}
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: newLocation ? '#059669' : '#1d5cab' }}>
                    {newLocation ? translate('map.location-confirmed') : translate('map.set-your-location')}
                  </p>
                  {!newLocation && (
                    <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>
                      {translate('map.search-address-click-map')}
                    </p>
                  )}
                </div>
              </div>

              {!newLocation ? (
                <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px', background: 'white' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      placeholder={translate('map.enter-address')}
                      className="map-input"
                      style={{ flex: 1, padding: '10px 12px', borderRadius: '8px', fontSize: '16px', border: '1.5px solid #e2e8f0', outline: 'none', fontFamily: "'DM Sans', sans-serif" }}
                      value={addressQuery}
                      onChange={(e) => setAddressQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddressSearch()}
                    />
                    <button
                      type="button"
                      onClick={handleAddressSearch}
                      disabled={isSearchingAddr}
                      style={{
                        padding: '10px 16px', borderRadius: '8px', border: 'none',
                        background: '#1d5cab', color: 'white', fontSize: '14px', fontWeight: '700',
                        cursor: isSearchingAddr ? 'not-allowed' : 'pointer', fontFamily: "'DM Sans', sans-serif",
                        opacity: isSearchingAddr ? 0.6 : 1, minWidth: '60px',
                      }}
                    >
                      {isSearchingAddr ? '…' : translate('map.search')}
                    </button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8' }}>OR</span>
                    <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
                  </div>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748b', textAlign: 'center' }}>
                    {isMobile ? translate('map.tap-map-drop-pin') || 'Tap the map above to place a pin.' : translate('map.click-map-drop-pin')}
                  </p>
                </div>
              ) : (
                <div style={{ padding: '12px 16px', background: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <p style={{ margin: '0 0 2px', fontSize: '12px', color: '#64748b' }}>
                      <b style={{ color: '#0f172a' }}>{translate('map.coords')}</b> {newLocation[0].toFixed(4)}, {newLocation[1].toFixed(4)}
                    </p>
                    <p style={{ margin: 0, fontSize: '11px', color: '#64748b', maxWidth: '240px' }}>{addressQuery}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNewLocation(null)}
                    style={{ fontSize: '12px', color: '#ef4444', fontWeight: '700', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0, marginLeft: '8px' }}
                  >
                    {translate('map.change')}
                  </button>
                </div>
              )}
            </div>

            {/* Business Name */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {translate('map.business-name')}*
              </label>
              <input
                className="map-input"
                style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '16px', fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box' }}
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            {/* Category */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {translate('map.category')}*
              </label>
              <select
                style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '16px', fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box', background: 'white', appearance: 'auto' }}
                value={formData.tier}
                onChange={e => setFormData({ ...formData, tier: e.target.value })}
              >
                <option value="">{translate('general.select')}</option>
                {categoryTypes.map(({ key, labelKey }) => (
                  <option key={key} value={key}>{translate(labelKey)}</option>
                ))}
              </select>
            </div>

            {/* Description — provider only */}
            {user?.role === 'provider' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {translate('map.description')}*
                </label>
                <textarea
                  className="map-input"
                  style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '16px', fontFamily: "'DM Sans', sans-serif", height: '100px', resize: 'none', boxSizing: 'border-box' }}
                  value={formData.desc}
                  onChange={e => setFormData({ ...formData, desc: e.target.value })}
                  required
                />
              </div>
            )}

            {/* Website — provider only */}
            {user?.role === 'provider' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {translate('map.website')}
                </label>
                <input
                  type="text"
                  placeholder={translate('map.website-placeholder')}
                  className="map-input"
                  style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '16px', fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box' }}
                  value={formData.website}
                  onChange={e => setFormData({ ...formData, website: e.target.value })}
                  onBlur={() => {
                    let v = formData.website.trim();
                    if (!v) return;
                    if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(v)) setFormData(prev => ({ ...prev, website: 'https://' + v }));
                  }}
                />
              </div>
            )}

            {/* Business Hours — provider only */}
            {user?.role === 'provider' && (
              <div style={{ marginBottom: '16px' }}>
                <HoursInput
                  value={formData.businessHours}
                  onChange={bh => setFormData({ ...formData, businessHours: bh })}
                />
              </div>
            )}

            {/* Contact Section */}
            <div style={{ borderRadius: '12px', border: '1.5px solid #e2e8f0', overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <p style={{ margin: 0, fontSize: '12px', fontWeight: '800', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {translate('map.contact')}*
                </p>
              </div>
              <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <input
                  type="tel"
                  placeholder={translate('map.phone')}
                  className="map-input"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #e2e8f0', fontSize: '16px', fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box' }}
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                />
                <input
                  type="email"
                  placeholder={translate('map.email')}
                  className="map-input"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #e2e8f0', fontSize: '16px', fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box' }}
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                />
                {/* Preferred contact — provider only */}
                {user?.role === 'provider' && (
                  <div>
                    <span style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>
                      {translate('map.preferred-contact')}
                    </span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {[
                        { value: 'email', label: translate('map.email'), color: '#3b82f6' },
                        { value: 'phone', label: translate('map.phone'), color: '#10b981' },
                        { value: 'both',  label: translate('map.both'),  color: '#8b5cf6' },
                      ].map(opt => (
                        <label
                          key={opt.value}
                          style={{
                            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            gap: '6px', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer',
                            border: `1.5px solid ${formData.preference === opt.value ? opt.color : '#e2e8f0'}`,
                            background: formData.preference === opt.value ? opt.color + '14' : 'white',
                            fontSize: '12px', fontWeight: '700', color: formData.preference === opt.value ? opt.color : '#64748b',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          <input
                            type="radio"
                            name="d-pref"
                            value={opt.value}
                            checked={formData.preference === opt.value}
                            onChange={e => setFormData({ ...formData, preference: e.target.value })}
                            style={{ display: 'none' }}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Sticky submit footer — always visible, never scrolls away ── */}
          <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', flexShrink: 0, background: 'white' }}>
            <button
              type="submit"
              disabled={!newLocation || isLoading}
              style={{
                width: '100%', padding: '14px', borderRadius: '12px', border: 'none',
                background: !newLocation ? '#e2e8f0' : isLoading ? '#93c5fd' : 'linear-gradient(135deg, #1d5cab, #2372bd)',
                color: !newLocation ? '#94a3b8' : 'white',
                fontSize: '15px', fontWeight: '800', cursor: (!newLocation || isLoading) ? 'not-allowed' : 'pointer',
                fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.02em',
                boxShadow: (!newLocation || isLoading) ? 'none' : '0 4px 16px rgba(29,92,171,0.35)',
                transition: 'all 0.2s ease',
              }}
            >
              {isLoading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <svg style={{ width: '18px', height: '18px', animation: 'spin 1s linear infinite' }} viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3"/>
                    <path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="3" strokeLinecap="round"/>
                  </svg>
                  {translate('buttons.processing')}
                </span>
              ) : translate('buttons.submit-request')}
            </button>
          </div>
        </form>
      </div>


      {/* ─── MOBILE: horizontal category strip pinned to the bottom ─── */}
      <div className="mobile-category-strip" style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 1100,
        background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(12px)',
        borderTop: '1px solid rgba(0,0,0,0.07)',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.08)',
      }}>
        <div className="no-scrollbar" style={{ display: 'flex', overflowX: 'auto', gap: '8px', padding: '10px 12px' }}>
          <button
            onClick={() => setSelectedTiers([])}
            style={{
              flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: '4px', padding: '8px 12px', borderRadius: '12px',
              minWidth: '56px', minHeight: '60px', cursor: 'pointer',
              background: selectedTiers.length === 0 ? '#1d5cab' : 'white',
              color: selectedTiers.length === 0 ? 'white' : '#64748b',
              border: `1.5px solid ${selectedTiers.length === 0 ? '#1d5cab' : '#e2e8f0'}`,
            }}
          >
            <Squares2X2Icon style={{ width: '20px', height: '20px' }} />
            <span style={{ fontSize: '10px', fontWeight: '700', lineHeight: 1 }}>{translate('map.all')}</span>
          </button>
          {Object.entries(TIER_META).map(([tier, meta]) => {
            if (tier === 'All') return null;
            const isActive = selectedTiers.includes(tier);
            const Icon = meta.icon;
            return (
              <button
                key={tier}
                onClick={() => setSelectedTiers(prev =>
                  prev.includes(tier) ? prev.filter(t => t !== tier) : [...prev, tier]
                )}
                style={{
                  flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', gap: '4px', padding: '8px 12px', borderRadius: '12px',
                  minWidth: '56px', minHeight: '60px', cursor: 'pointer',
                  background: isActive ? meta.color : 'white',
                  color: isActive ? 'white' : '#64748b',
                  border: `1.5px solid ${isActive ? meta.color : '#e2e8f0'}`,
                }}
              >
                <Icon style={{ width: '20px', height: '20px' }} />
                <span style={{ fontSize: '10px', fontWeight: '700', lineHeight: 1 }}>
                  {translate(`map.${tier.toLowerCase()}`)}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── MOBILE: FAB "+" button ─── */}
      {!isDrawerOpen && (
        <div className="mobile-fab" style={{ position: 'absolute', bottom: '140px', right: '24px', zIndex: 1200 }}>
          <button
            onClick={startAddingProcess}
            aria-label={translate('map.add-service-hint')}
            style={{
              width: '56px', height: '56px',
              borderRadius: '50%', border: 'none',
              background: 'linear-gradient(135deg, #1d5cab 0%, #2372bd 100%)',
              color: 'white', cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(29,92,171,0.45)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '24px', height: '24px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
      )}

      {/* ══ MAP ═════════════════════════════════════════════════════════════════ */}
      <MapContainer
        center={userPos}
        zoom={13}
        zoomControl={false}
        style={{ position: 'relative', zIndex: 0, height: '100%', width: '100%' }}
        className="h-full w-full"
      >
        {/* Cleaner, more modern map tile (CartoDB Voyager) */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url={`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${process.env.REACT_APP_CARTO_API_KEY}`}
        />

        {!highlightProvider && <MapRecenter location={userPos} />}
        {highlightProvider && (
          <FlyToProvider
            provider={highlightProvider}
            onDone={() => {
              setHighlightProvider(null);
              hasDeepLink.current = false;
              const sp = new URLSearchParams(window.location.search);
              if (sp.has('provider')) {
                sp.delete('provider');
                const newUrl = sp.toString() ? `${window.location.pathname}?${sp}` : window.location.pathname;
                window.history.replaceState(null, '', newUrl);
              }
            }}
          />
        )}
        <ZoomControl position="bottomright" />
        <LocationSelector isAdding={isAddingMode} onLocationFound={handleMapClick} />

        <ShieldedMarkers
          groupedProviders={groupedProviders}
          favorites={favorites}
          onToggleFav={handleToggleFav}
          translateFn={translate}
        />

        {newLocation && (
          <Marker position={newLocation} icon={pendingIcon}>
            <Popup className="map-popup-custom">
              <div style={{ padding: '12px 16px', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: '600', color: '#7c3aed' }}>
                📍 {translate('map.new-location-set')}
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>
    </div>
  );
};

export default MapView;
